export const SESSION_EXPIRED_EVENT = "misia:session-expired";

const IGNORED_EXACT_PATHS = new Set([
  "/api/v1/auth/refresh",
  "/api/v1/config/public",
  "/auth/session",
  "/auth/logout",
  "/auth/change-password",
  "/auth/exchange-token",
]);

const IGNORED_PREFIXES = [
  "/api/v1/registration",
  "/api/v1/public",
  "/auth/mfa",
  "/auth/oidc",
  "/auth/password-reset",
];

let originalFetch: typeof fetch | null = null;
let installed = false;
let sessionExpiredEmitted = false;

function requestPath(input: RequestInfo | URL): string | null {
  const base =
    typeof window !== "undefined" && window.location?.origin
      ? window.location.origin
      : "http://localhost";

  try {
    if (typeof input === "string" || input instanceof URL) {
      return new URL(input, base).pathname;
    }
    return new URL(input.url, base).pathname;
  } catch {
    return null;
  }
}

export function isSessionExpiryCandidate(input: RequestInfo | URL): boolean {
  const path = requestPath(input);
  if (!path) return false;
  if (IGNORED_EXACT_PATHS.has(path)) return false;
  if (IGNORED_PREFIXES.some((prefix) => path.startsWith(prefix))) return false;
  return path.startsWith("/api/") || path.startsWith("/auth/");
}

export function emitSessionExpired(): void {
  if (sessionExpiredEmitted || typeof window === "undefined") return;
  sessionExpiredEmitted = true;
  window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
}

export function resetSessionExpiredSignal(): void {
  sessionExpiredEmitted = false;
}

export function subscribeSessionExpired(handler: () => void): () => void {
  if (typeof window === "undefined") return () => {};
  window.addEventListener(SESSION_EXPIRED_EVENT, handler);
  return () => window.removeEventListener(SESSION_EXPIRED_EVENT, handler);
}

export function installSessionExpiryFetchInterceptor(): () => void {
  if (installed || typeof globalThis.fetch !== "function") return () => {};

  originalFetch = globalThis.fetch.bind(globalThis);
  installed = true;

  globalThis.fetch = (async (input: RequestInfo | URL, init?: RequestInit) => {
    const response = await originalFetch!(input, init);
    if (response.status === 401 && isSessionExpiryCandidate(input)) {
      queueMicrotask(emitSessionExpired);
    }
    return response;
  }) as typeof fetch;

  return () => {
    if (installed && originalFetch) {
      globalThis.fetch = originalFetch;
    }
    originalFetch = null;
    installed = false;
    sessionExpiredEmitted = false;
  };
}

export function resetSessionExpiryFetchInterceptorForTests(): void {
  if (installed && originalFetch) {
    globalThis.fetch = originalFetch;
  }
  originalFetch = null;
  installed = false;
  sessionExpiredEmitted = false;
}
